import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { PageTitle } from '@/components/layout/PageTitle';
import { Button } from '@/components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus } from 'lucide-react';
import ProvisionPartnerDrawer from './components/provision-partner-drawer';

interface Partner {
    organizationId: string;
    applicationId: string;
    name: string;
    appKey: string;
    status: string;
    created: string;
}

export default function PartnersListPage() {
    const { fetchWithAuth } = useAuth();
    const [partners, setPartners] = useState<Partner[]>([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const load = () => {
        setLoading(true);
        fetchWithAuth('/api/partners')
            .then((res) => res.json())
            .then((data) => setPartners(data.data ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [fetchWithAuth]);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <PageTitle
                    title="Partners"
                    subtitle="White-label partner organizations and their applications"
                />
                <Button onClick={() => setDrawerOpen(true)} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Provision Partner
                </Button>
            </div>

            {loading ? (
                <div className="text-muted-foreground text-sm">Loading partners...</div>
            ) : partners.length === 0 ? (
                <div className="text-muted-foreground text-sm">
                    No partners provisioned yet. Use the Provision Partner button to create one.
                </div>
            ) : (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>App Key</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Created</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {partners.map((p) => (
                                <TableRow key={p.organizationId}>
                                    <TableCell className="font-medium">{p.name}</TableCell>
                                    <TableCell className="font-mono text-sm">{p.appKey}</TableCell>
                                    <TableCell>
                                        <Badge variant={p.status === 'Active' ? 'default' : 'secondary'}>
                                            {p.status ?? 'Active'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-sm">
                                        {p.created ? new Date(p.created).toLocaleDateString() : '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <ProvisionPartnerDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                onSuccess={() => { setDrawerOpen(false); load(); }}
            />
        </div>
    );
}
